import { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  ReferenceInput,
  SelectInput,
  AutocompleteInput,
  TextInput,
  BooleanInput,
  CheckboxGroupInput,
  TranslatableInputs,
  FormDataConsumer,
  required,
  TopToolbar,
  ListButton,
  useNotify,
  useGetList,
  useGetOne,
  useDataProvider,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, MenuItem, TextField as MuiTextField, Typography } from '@mui/material';
import { APP_LOCALES, DEFAULT_LOCALE } from '../../locales';
import type { Client } from '../../types/client';
import type { BuildReadiness, SkippedClient, Template } from '../../types';
import type { LogtoDataProvider } from '../../dataProvider';

const CreateActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);

// Environment the app is built for (dev / stage / prod), offered when the
// template(s) of the build support more than prod. Only prod builds can be
// released; other environments switch the build to testing.
const EnvironmentInput = ({ templateKeys }: { templateKeys: string[] }) => {
  const { setValue, watch } = useFormContext();
  const environment: string = watch('environment') || 'prod';
  const skipPublish: boolean = watch('skipPublish');
  const { data: templates } = useGetList<Template>('templates', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'key', order: 'ASC' },
  });
  const keys = templateKeys.join(',');
  const environments = Array.from(
    new Set(
      (templates || [])
        .filter((t) => keys.split(',').includes(t.key))
        .flatMap((t) => t.environments || ['prod'])
    )
  );
  const options = environments.includes('prod') ? environments : ['prod', ...environments];

  useEffect(() => {
    if (!options.includes(environment)) setValue('environment', 'prod');
  }, [options.join(','), environment, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (environment !== 'prod' && !skipPublish) setValue('testingOnly', true);
  }, [environment, skipPublish, setValue]);

  if (options.length <= 1) return null;
  return (
    <SelectInput
      source="environment"
      label="Environment"
      choices={options.map((e) => ({ id: e, name: e }))}
      helperText="Only prod builds can be released; other environments are built only or uploaded for testing"
      validate={required()}
      sx={{ minWidth: 220 }}
    />
  );
};

const validateClientSelection = (value: string[] | undefined) =>
  !value || value.length === 0 ? 'Select at least one client' : undefined;

// Checkbox list of active clients for mass rebuild, narrowed by template and
// template (platform) version. Every visible client starts checked; the user
// unchecks clients to exclude them from the batch.
const MassClientSelector = () => {
  const { data: clients, isLoading } = useGetList<Client>('clients', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
    filter: { status: 'active' },
  });
  const { data: templates } = useGetList<Template>('templates', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'key', order: 'ASC' },
  });
  const { setValue, watch } = useFormContext();
  const selected: string[] | undefined = watch('clientIds');
  const selectedTemplates = Array.from(
    new Set((clients || []).filter((c) => selected?.includes(c.id)).map((c) => c.templateKey))
  );
  const [templateKey, setTemplateKey] = useState('');
  const [coreVersion, setCoreVersion] = useState('');

  const byTemplate = (clients || []).filter((c) => !templateKey || c.templateKey === templateKey);
  const visible = byTemplate.filter((c) => !coreVersion || c.currentCoreVersion === coreVersion);
  const versions = Array.from(new Set(byTemplate.map((c) => c.currentCoreVersion).filter(Boolean))) as string[];
  const visibleKey = visible.map((c) => c.id).join(',');

  // (Re)check every visible client when the list loads or the filters change.
  useEffect(() => {
    if (clients) {
      setValue('clientIds', visibleKey ? visibleKey.split(',') : [], { shouldDirty: false });
    }
  }, [clients, visibleKey, setValue]);

  if (isLoading || !clients) return null;

  return (
    <>
      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <MuiTextField
          select
          size="small"
          label="Template"
          value={templateKey}
          onChange={(e) => {
            setTemplateKey(e.target.value);
            setCoreVersion('');
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All templates</MenuItem>
          {(templates || []).map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name} ({t.key})
            </MenuItem>
          ))}
        </MuiTextField>
        <MuiTextField
          select
          size="small"
          label="Template version"
          value={coreVersion}
          onChange={(e) => setCoreVersion(e.target.value)}
          sx={{ minWidth: 200 }}
          helperText="Version of the last successful build"
        >
          <MenuItem value="">Any version</MenuItem>
          {versions.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </MuiTextField>
      </Box>
      <CheckboxGroupInput
        source="clientIds"
        label="Clients to rebuild"
        choices={visible.map((c) => ({
          id: c.id,
          name: `${c.name} — ${c.templateKey}${c.currentCoreVersion ? ` ${c.currentCoreVersion}` : ''}`,
        }))}
        row={false}
        validate={validateClientSelection}
      />
      <Typography variant="body2" sx={{ mb: 2 }}>
        Will build: {selected?.length ?? 0} of {visible.length}. Clients that are not ready
        (missing template settings) or whose template has no selected environment are skipped
        and listed after queuing.
      </Typography>
      <EnvironmentInput templateKeys={selectedTemplates} />
    </>
  );
};

// Template for a single build: the client's own by default, or another one for
// a test build. Test builds default to "build only" so they don't reach the stores.
const SingleBuildTemplate = ({ clientId }: { clientId?: string }) => {
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const { setValue, watch } = useFormContext();
  const templateKey: string = watch('templateKey') || '';
  const { data: client } = useGetOne<Client>('clients', { id: clientId || '' }, { enabled: !!clientId });
  const { data: templates } = useGetList<Template>('templates', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'key', order: 'ASC' },
  });
  const [readiness, setReadiness] = useState<BuildReadiness | null>(null);
  const isTestBuild = !!templateKey && !!client && templateKey !== client.templateKey;

  useEffect(() => {
    setReadiness(null);
    if (!clientId) return;
    dataProvider
      .getBuildReadiness(clientId, templateKey || undefined)
      .then(setReadiness)
      .catch(() => setReadiness(null));
  }, [clientId, templateKey, dataProvider]);

  useEffect(() => {
    if (isTestBuild) setValue('skipPublish', true);
  }, [isTestBuild, setValue]);

  if (!clientId) return null;

  return (
    <>
      <SelectInput
        source="templateKey"
        label="Template"
        emptyText={client ? `Client's template (${client.templateKey})` : "Client's template"}
        choices={(templates || []).map((t) => ({ id: t.key, name: `${t.name} (${t.key})` }))}
        helperText="Another template builds a test version; the client's template is not changed"
        sx={{ minWidth: 320 }}
      />
      <EnvironmentInput templateKeys={[templateKey || client?.templateKey || '']} />
      {isTestBuild && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Test build on template {templateKey} (client is on {client?.templateKey}). Publishing is
          switched off by default — the build would go to the stores as a regular update.
        </Alert>
      )}
      {readiness && !readiness.ready && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Not ready to build on template {readiness.templateKey}:{' '}
          {readiness.missing.map((m) => m.field).join(', ')}
          {isTestBuild && '. Fill this template\'s settings in the client configuration (template selector).'}
        </Alert>
      )}
    </>
  );
};

export const BuildCreate = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  return (
    <Create
      mutationOptions={{
        onSuccess: (data: any) => {
          const skipped: SkippedClient[] = data?.skipped || [];
          if (skipped.length > 0) {
            notify(
              `Queued ${data.queuedBuilds ?? 0} builds. Skipped (not ready): ${skipped.map((c) => c.name).join(', ')}`,
              { type: 'warning', autoHideDuration: 15000 }
            );
          } else {
            notify('Build queued', { type: 'success' });
          }
          if (data?.triggerType === 'mass_rebuild' || !data?.id) {
            navigate('/builds');
            return;
          }
          navigate(`/builds/${data.id}/show`);
        },
      }}
      actions={<CreateActions />}
    >
      <SimpleForm
        defaultValues={{
          triggerType: 'manual',
          skipPublish: false,
          publishAndroid: true,
          publishIos: true,
          testingOnly: false,
          environment: 'prod',
        }}
      >
        <SelectInput
          source="triggerType"
          choices={[
            { id: 'manual', name: 'Manual' },
            { id: 'mass_rebuild', name: 'Mass Rebuild' },
          ]}
          validate={required()}
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData.triggerType === 'mass_rebuild' ? (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Mass rebuild queues a build for every checked client. Release
                  notes and publish settings apply to all of them.
                </Alert>
                <MassClientSelector />
              </>
            ) : (
              <>
                <ReferenceInput source="clientId" reference="clients" perPage={100}>
                  <AutocompleteInput
                    optionText="name"
                    filterToQuery={(searchText: string) => ({ q: searchText })}
                    validate={required()}
                  />
                </ReferenceInput>
                <SingleBuildTemplate clientId={formData.clientId} />
              </>
            )
          }
        </FormDataConsumer>
        <TranslatableInputs locales={APP_LOCALES} defaultLocale={DEFAULT_LOCALE}>
          <TextInput
            source="releaseNotes"
            label="What's New"
            multiline
            fullWidth
            rows={4}
          />
        </TranslatableInputs>
        <FormDataConsumer>
          {({ formData }) => (
            <>
              <BooleanInput
                source="skipPublish"
                label="Build only (skip publishing to stores)"
              />
              {!formData.skipPublish && (
                <>
                  <BooleanInput
                    source="testingOnly"
                    label="For testing only (Google Play internal testing + TestFlight)"
                    helperText={
                      formData.environment && formData.environment !== 'prod'
                        ? `Required for the ${formData.environment} environment: only prod builds can be released`
                        : 'Uploaded to the stores for testers: no production release, no App Store review'
                    }
                    disabled={!!formData.environment && formData.environment !== 'prod'}
                  />
                  <BooleanInput
                    source="publishAndroid"
                    label={formData.testingOnly ? 'Upload to Google Play internal testing' : 'Publish to Google Play'}
                  />
                  <BooleanInput
                    source="publishIos"
                    label={formData.testingOnly ? 'Upload to TestFlight' : 'Publish to App Store'}
                  />
                </>
              )}
            </>
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  );
};
